import { useCallback, useEffect, useState } from 'react';
import {
  fetchAdminCategories,
  setCategoryActive,
  setSubcategoryActive,
} from '../../api/admin/categories.admin';
import { csrf } from '../../api/auth';
import './AdminCategories.css';

export default function AdminCategories() {
  const [items, setItems] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  const [busy, setBusy] = useState('');
  /** Раскрытые категории (по умолчанию все свёрнуты) */
  const [openIds, setOpenIds] = useState(() => new Set());

  const load = useCallback(async () => {
    setLoading(true);
    setError('');
    try {
      const data = await fetchAdminCategories();
      setItems(Array.isArray(data) ? data : []);
    } catch (e) {
      setError(e?.response?.data?.error || 'Не удалось загрузить каталог');
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => { load(); }, [load]);

  function toggleOpen(id) {
    const key = String(id);
    setOpenIds((prev) => {
      if (prev.has(key)) return new Set();
      return new Set([key]);
    });
  }

  async function toggleCategory(cat) {
    const next = !(cat.isActive !== false);
    const key = `c-${cat._id}`;
    setBusy(key);
    try {
      await csrf();
      await setCategoryActive(cat._id, next);
      await load();
    } catch (e) {
      setError(e?.response?.data?.error || 'Не удалось изменить категорию');
    } finally {
      setBusy('');
    }
  }

  async function toggleSub(cat, sub) {
    const next = !(sub.isActive !== false);
    const key = `s-${sub._id}`;
    setBusy(key);
    try {
      await csrf();
      await setSubcategoryActive(cat._id, sub._id, next);
      await load();
    } catch (e) {
      setError(e?.response?.data?.error || 'Не удалось изменить подкатегорию');
    } finally {
      setBusy('');
    }
  }

  if (loading) return <p>Загрузка…</p>;

  return (
    <div className="admin-cats">
      <h2 className="admin-page-title">Каталог: категории</h2>
      <p className="admin-cats__lead">
        Скрытые категории и подкатегории не показываются на витрине. Товары в них сохраняются.
      </p>
      {error && <p className="admin-cats__error">{error}</p>}

      <div className="admin-cats__list">
        {items.map((cat) => {
          const catVisible = cat.isActive !== false;
          const isOpen = openIds.has(String(cat._id));
          const subCount = (cat.subcategories || []).length;
          return (
            <section
              key={cat._id}
              className={`admin-cats__card ${catVisible ? '' : 'is-hidden'} ${isOpen ? 'is-open' : ''}`}
            >
              <div className="admin-cats__row">
                <button
                  type="button"
                  className="admin-cats__toggle"
                  onClick={() => toggleOpen(cat._id)}
                  aria-expanded={isOpen}
                >
                  <span className="admin-cats__chevron" aria-hidden>{isOpen ? '▾' : '▸'}</span>
                  <span className="admin-cats__title-wrap">
                    <strong>{cat.name?.ru || cat.slug}</strong>
                    <span className="admin-cats__meta">
                      {cat.slug}
                      {subCount ? ` · ${subCount} подкат.` : ''}
                    </span>
                  </span>
                </button>
                <button
                  type="button"
                  className={catVisible ? 'edit-btn' : 'muted'}
                  disabled={busy === `c-${cat._id}`}
                  onClick={() => toggleCategory(cat)}
                >
                  {catVisible ? 'Скрыть' : 'Показать'}
                </button>
              </div>

              {isOpen && (
                <div className="admin-cats__panel">
                  <ul className="admin-cats__subs">
                    {(cat.subcategories || []).map((sub) => {
                      const subVisible = sub.isActive !== false;
                      return (
                        <li key={sub._id} className={subVisible ? '' : 'is-hidden'}>
                          <span>{sub.name?.ru || sub.slug}</span>
                          <button
                            type="button"
                            className={subVisible ? 'edit-btn' : 'muted'}
                            disabled={busy === `s-${sub._id}`}
                            onClick={() => toggleSub(cat, sub)}
                          >
                            {subVisible ? 'Скрыть' : 'Показать'}
                          </button>
                        </li>
                      );
                    })}
                    {!subCount && (
                      <li className="admin-cats__empty">Нет подкатегорий</li>
                    )}
                  </ul>
                </div>
              )}
            </section>
          );
        })}
      </div>
    </div>
  );
}
